import { el, tel, StatusLine } from "./dom.ts";
import { t, onLangChange } from "./i18n.ts";
import { getState, setState, subscribe } from "./state.ts";
import { createSection, type Section } from "./section.ts";
import { SECTION_ICON, moduleIconSvg } from "./icons.ts";
import { MODULES } from "../modules/registry.ts";
import type { ModuleId } from "../auth/config.ts";
import { scopesForModules, probeGranted } from "../auth/scopes.ts";
import { acquireTokenInteractive } from "../auth/broker.ts";

/**
 * What to export → approve access, as one always-visible dashboard section
 * (ARCHITECTURE.md §15 steps 3/4, §4).
 *
 * Modules are shown as an icon grid the user picks from directly, ninite-
 * style — everything visible and clickable at once, rather than behind a
 * step that has to be reached first. The only thing actually gated is the
 * Export panel's Start button, which stays disabled with an inline reason
 * until sign-in, selection, access and a destination folder are all in
 * place; nothing here needs to be "reached" before it can be used.
 *
 * Connection test and preflight checks run as the first phase of the
 * Export panel's Start/Resume, not here — see export-panel.ts.
 */

const STORAGE_KEY = "o365x.selectedModules";
const AVAILABLE_IDS = MODULES.filter((m) => m.available).map((m) => m.id);

function loadSelection(): ModuleId[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return [];
        }
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        const availableIds = new Set(AVAILABLE_IDS);
        return parsed.filter(
            (id): id is ModuleId => typeof id === "string" && availableIds.has(id as ModuleId)
        );
    } catch {
        return [];
    }
}

function saveSelection(ids: ModuleId[]): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

function isUserCancellation(err: unknown): boolean {
    const code = (err as { errorCode?: string } | null)?.errorCode;
    return code === "user_cancelled" || code === "popup_window_error";
}

export function createSetupPanel(): Section {
    const panel = createSection({
        id: "panel-setup",
        titleKey: "modules.section",
        introKey: "setup.intro",
        icon: SECTION_ICON.setup
    });

    /* ---- module grid ---------------------------------------------------- */

    const grid = el("ul", { class: "module-grid" });
    const checkboxes = new Map<ModuleId, HTMLInputElement>();

    const applySelection = (ids: readonly ModuleId[]): void => {
        saveSelection([...ids]);
        setState({ selectedModules: [...ids] });
        for (const [id, box] of checkboxes) {
            box.checked = ids.includes(id);
        }
    };

    const selectAllButton = tel("button", "modules.selectAll", { type: "button" });
    const selectNoneButton = tel("button", "modules.selectNone", { type: "button" });
    selectAllButton.addEventListener("click", () => applySelection(AVAILABLE_IDS));
    selectNoneButton.addEventListener("click", () => applySelection([]));

    for (const definition of MODULES) {
        const checkbox = el("input", {
            type: "checkbox",
            id: `module-${definition.id}`,
            class: "module-card-check"
        }) as HTMLInputElement;
        checkbox.disabled = !definition.available;
        checkboxes.set(definition.id, checkbox);

        checkbox.addEventListener("change", () => {
            const current = new Set(getState().selectedModules);
            if (checkbox.checked) {
                current.add(definition.id);
            } else {
                current.delete(definition.id);
            }
            applySelection([...current]);
        });

        // Description and the plain-language permission it needs are merged
        // into one line rather than shown in a separate "what you're about
        // to grant" list elsewhere — the two were saying almost the same
        // thing twice, in two different places on the page. Two separate
        // data-i18n spans (not one concatenated string) so each half keeps
        // updating itself on a language switch — applyTranslations() can
        // only ever replace one key's element at a time.
        const descriptionParts: (Node | string)[] = [tel("span", definition.descriptionKey)];
        if (definition.available) {
            descriptionParts.push(" · ", tel("span", definition.permissionKey));
        }

        const icon = el("span", { class: "module-card-icon" });
        icon.innerHTML = moduleIconSvg(definition.id);

        const top = el("span", { class: "module-card-top" }, [icon, checkbox]);
        if (!definition.available) {
            top.append(tel("span", "modules.unavailable", { class: "badge" }));
        }

        const label = el("label", { for: `module-${definition.id}`, class: "module-card" }, [
            top,
            tel("span", definition.labelKey, { class: "module-card-name" }),
            el("span", { class: "module-card-desc" }, descriptionParts)
        ]);

        const item = el(
            "li",
            { class: definition.available ? "module-card-item" : "module-card-item unavailable" },
            [label]
        );
        grid.append(item);
    }

    /* ---- approve access ------------------------------------------------ */

    const grantButton = tel("button", "permissions.grant", { type: "button", class: "primary" });
    const grantStatus = new StatusLine();

    grantButton.addEventListener("click", async () => {
        const selected = getState().selectedModules;
        if (selected.length === 0) {
            return;
        }

        grantButton.disabled = true;
        grantStatus.set(t("permissions.requesting"), "muted");

        try {
            // From this click's user gesture — a popup cannot be opened from
            // inside an async chain that failed.
            await acquireTokenInteractive(scopesForModules(selected));
            const granted = await probeGranted(selected);
            setState({ grantedModules: granted });
            grantStatus.clear();
        } catch (err) {
            if (isUserCancellation(err)) {
                grantStatus.set(t("permissions.cancelled"), "warn");
            } else {
                grantStatus.set(
                    t("permissions.error", {
                        message: err instanceof Error ? err.message : t("error.unknown")
                    }),
                    "err"
                );
            }
        } finally {
            grantButton.disabled = false;
        }
    });

    panel.body.append(
        el("div", { class: "actions" }, [selectAllButton, selectNoneButton]),
        grid,
        el("div", { class: "actions" }, [grantButton]),
        grantStatus.node
    );

    // Restore a previous selection before wiring the subscription, so the
    // checkboxes and shared state agree from the first paint.
    const restored = loadSelection();
    if (restored.length > 0) {
        setState({ selectedModules: restored });
        for (const id of restored) {
            const box = checkboxes.get(id);
            if (box) {
                box.checked = true;
            }
        }
    }

    let lastSelectionKey = "";
    let lastAccountId: string | null = null;

    const renderSummary = () => {
        const state = getState();
        const selected = state.selectedModules;
        const signedIn = state.account !== null;
        const allGranted =
            selected.length > 0 && selected.every((id) => state.grantedModules.includes(id));
        const complete = signedIn && selected.length > 0 && allGranted;

        let summary: string;
        if (!signedIn) {
            summary = t("modules.signInFirst");
        } else if (selected.length === 0) {
            summary = t("modules.noneSelected");
        } else if (!allGranted) {
            summary = t("permissions.needed");
        } else {
            summary = t("setup.confirmedSummary");
        }
        panel.setState(complete, summary);
    };

    subscribe((state) => {
        const selected = state.selectedModules;
        const selectionKey = [...selected].sort().join(",");
        const accountId = state.account?.homeAccountId ?? null;

        const selectionChanged = selectionKey !== lastSelectionKey;
        const accountChanged = accountId !== lastAccountId;
        lastSelectionKey = selectionKey;
        lastAccountId = accountId;

        const signedIn = state.account !== null;
        const allGranted =
            selected.length > 0 && selected.every((id) => state.grantedModules.includes(id));

        grantButton.hidden = allGranted;
        grantButton.disabled = !signedIn || selected.length === 0;

        if (signedIn && selected.length > 0 && (accountChanged || selectionChanged)) {
            void (async () => {
                const granted = await probeGranted(selected);
                setState({ grantedModules: granted });
            })();
        }

        renderSummary();
    });

    onLangChange(renderSummary);

    return panel;
}
